import { useContext, useEffect, useState } from "react";
import { Box, IconButton, Icon, LinearProgress, Typography } from "@mui/material";
import { AuthContext } from "src/utils/AuthContext.js";
import { customEvent } from "src/utils/gtag.js";
import { hasValidConsumerSubscription } from "src/services";
import { UpgradeConsumerButton } from "src/components/upgrades/consumer/UpgradeConsumerButton.js";
import { FREE_CALENDAR_LIMIT, dismissUpsell, isUpsellDismissed, nextStanzaPlusProp } from "./stanzaPlusProps.js";

const OFFER_FINE = "$0 today, then $2.50/mo. Cancel anytime.";
const PERKS = ["Unlimited calendars", "Personalize anything", "Custom reminders"];

/** Fires a Stanza+ upsell analytics event with the standard logged-in payload. */
const trackUpsell = (name, label, user) =>
  customEvent({
    name,
    category: "stanza_plus_upsell",
    label,
    data: user ? { user_id: user._id, user_email: user.email } : {},
  });

/** Shared dismiss control; starts the cooldown and hides the band. */
const DismissButton = ({ onDismiss, onInk = false }) => (
  <IconButton
    aria-label="Dismiss"
    onClick={onDismiss}
    size="small"
    sx={{ position: "absolute", top: 8, right: 8, color: onInk ? "rgba(255,255,255,0.7)" : "text.secondary" }}
  >
    <Icon baseClassName="material-symbols-outlined" sx={{ fontSize: "1.1rem" }}>
      close
    </Icon>
  </IconButton>
);

/**
 * In-feed Stanza+ upsell. Shown only to signed-in free users (members and
 * signed-out visitors render nothing). Each page owns a `variant`; the message
 * cycles evenly across the value-prop library so the same user sees a different
 * angle each impression. The standard `UpgradeConsumerButton` owns the CTA and
 * its tracking; `source` (page_variant_prop) tags the impression event and the
 * upgrade flow for attribution.
 *
 * @param {{ variant: "dark" | "light" | "contextual", page: string, sx?: object }} props
 *   `sx` overrides the outer container spacing so each surface can match its own
 *   vertical rhythm (the feed uses bottom margins; see-all uses top margins).
 */
const StanzaPlusBand = ({ variant = "light", page, sx = {} }) => {
  const { user } = useContext(AuthContext) ?? {};
  const [dismissed, setDismissed] = useState(false);

  // Pick the prop once per mount and advance the shared cursor. A lazy useState
  // initializer runs exactly once (unlike useMemo, whose factory React may
  // re-invoke), so the cursor advances by exactly one per impression.
  const [prop] = useState(() => nextStanzaPlusProp());
  const source = `${page}_${variant}_${prop.key}`;
  const eligible = Boolean(user) && !hasValidConsumerSubscription(user) && !dismissed && !isUpsellDismissed();

  useEffect(() => {
    if (eligible) trackUpsell("stanza_plus_upsell_impression", source, user);
    // Fire once per eligible mount; `source` is stable for the mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eligible, source]);

  if (!eligible) return null;

  const handleDismiss = () => {
    dismissUpsell();
    setDismissed(true);
    trackUpsell("stanza_plus_upsell_dismissed", source, user);
  };

  const ctaState = { source: `upsell_${source}` };
  const followingCount = user?.consumerSlotsUsed ?? user?.addedCalendars?.length ?? 0;
  const atLimit = followingCount >= FREE_CALENDAR_LIMIT;

  if (variant === "dark") {
    return (
      <Box
        data-testid="stanza-plus-band-dark"
        sx={{
          position: "relative",
          mb: { xs: 5, md: 7 },
          p: { xs: 3, md: 3.5 },
          borderRadius: "16px",
          background: "linear-gradient(120deg, #0d0d10, #26262c)",
          color: "#fff",
          display: "flex",
          alignItems: "center",
          gap: 3,
          flexWrap: "wrap",
          ...sx,
        }}
      >
        <DismissButton onDismiss={handleDismiss} onInk />
        <Box sx={{ flex: "1 1 340px" }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(255,255,255,0.62)" }}>
            Stanza+
          </Typography>
          <Typography variant="h2" sx={{ fontSize: { xs: "1.4rem", md: "1.55rem" }, fontWeight: 800, my: 0.5 }}>
            {prop.headline}
          </Typography>
          <Typography sx={{ fontSize: "15px", color: "rgba(255,255,255,0.72)", maxWidth: "52ch" }}>
            {prop.sub}
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mt: 1.5 }}>
            {PERKS.map((perk) => (
              <Typography key={perk} sx={{ fontSize: "14px", fontWeight: 600 }}>
                {perk}
              </Typography>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "stretch" }}>
          <UpgradeConsumerButton state={ctaState} variant="outlined" color="secondary" sx={{ width: "100%" }}>
            Start free trial
          </UpgradeConsumerButton>
          <Typography sx={{ fontSize: "14px", color: "rgba(255,255,255,0.6)", textAlign: "center" }}>{OFFER_FINE}</Typography>
        </Box>
      </Box>
    );
  }

  if (variant === "contextual") {
    return (
      <Box
        data-testid="stanza-plus-band-contextual"
        sx={{
          position: "relative",
          mb: { xs: 5, md: 7 },
          px: { xs: 3, md: 5 },
          py: { xs: 3, md: 3.5 },
          borderRadius: "16px",
          border: (theme) => `1.5px solid ${theme.palette.text.primary}`,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: { xs: 2, md: 3 },
          ...sx,
        }}
      >
        <DismissButton onDismiss={handleDismiss} />
        <Box sx={{ flex: "1 1 320px" }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "text.secondary" }}>
            Your calendars
          </Typography>
          <Typography variant="h2" sx={{ fontSize: "1.2rem", fontWeight: 800, mb: 0.5 }}>
            {atLimit
              ? "You've hit the free limit"
              : `You're following ${followingCount} of ${FREE_CALENDAR_LIMIT} free calendars`}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={Math.min(followingCount / FREE_CALENDAR_LIMIT, 1) * 100}
            sx={{ height: 10, borderRadius: 999, my: 1, backgroundColor: "overlay.black06", "& .MuiLinearProgress-bar": { backgroundColor: "text.primary", borderRadius: 999 } }}
          />
          <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
            {atLimit
              ? "You're at the 2-calendar free limit. Go unlimited with Stanza+, plus reminders and personalization."
              : "The free plan stops at 2 calendars. Stanza+ is unlimited, with reminders and personalization."}
          </Typography>
        </Box>
        <Box sx={{ flex: "0 0 auto" }}>
          <UpgradeConsumerButton state={ctaState} sx={{ width: "auto" }}>
            Start free trial
          </UpgradeConsumerButton>
        </Box>
      </Box>
    );
  }

  // variant === "light"
  return (
    <Box
      data-testid="stanza-plus-band-light"
      sx={{
        position: "relative",
        mb: { xs: 5, md: 7 },
        px: { xs: 3, md: 5 },
        py: { xs: 3, md: 3.5 },
        borderRadius: "16px",
        border: (theme) => `1.5px solid ${theme.palette.text.primary}`,
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: { xs: 2, md: 3 },
        ...sx,
      }}
    >
      <DismissButton onDismiss={handleDismiss} />
      <Box sx={{ flex: "1 1 auto", minWidth: 220 }}>
        <Typography sx={{ fontSize: "14px", fontWeight: 800, letterSpacing: "0.12em", textTransform: "uppercase", color: "text.secondary" }}>
          Stanza+
        </Typography>
        <Typography component="h2" sx={{ fontSize: { xs: "19px", md: "22px" }, fontWeight: 800, my: 0.25 }}>
          {prop.headline}
        </Typography>
        <Typography sx={{ fontSize: "15px", color: "text.secondary" }}>{prop.sub}</Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, alignItems: "stretch", flex: "0 0 auto" }}>
        <UpgradeConsumerButton state={ctaState} sx={{ width: "auto" }}>
          Start free trial
        </UpgradeConsumerButton>
        <Typography sx={{ fontSize: "14px", color: "text.secondary", textAlign: "center" }}>{OFFER_FINE}</Typography>
      </Box>
    </Box>
  );
};

export default StanzaPlusBand;
